'use client';

import { SkeletonBar, Price as SparkyPrice, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';

import { useDecisions, useOptimizely } from '@libs/common-next';

import { isProductAvailableForAutoship, useProduct } from '@/entities/product';
import { register, usePurchaseFormWatch } from '@/entities/purchase-form';
import { useAutoshipCustomerStatus } from '@/features/autoship-customer-status';
import { useAutoshipSubscription } from '@/widgets/product-details/subscribed-with-autoship';

import { getAutoshipDiscountMessages } from '../../lib';
import { useOneTimePrice } from '../../model';
import { FrequencyOptions } from '../FrequencyOptions';
import { AutoshipInfoDrawer } from './AutoshipInfoDrawer';
import { FrequencyCalculatorCta } from './FrequencyCalculatorCta';

import './PurchaseOptions.scss';

export const PurchaseOptions = () => {
    const { product } = useProduct();
    const autoshipFlag = useDecisions('ff_autoship');
    const isSubscribedIndicatorEnabled = useDecisions('x_as_sub_ind');
    const isPdpDynPxEnabled = useDecisions('x_pdp_dyn_px');
    const isFrequencyCalculatorEnabled = useDecisions('pdp_as_fr_calc');
    // Align with useGetDefaultValues: usePurchaseFormWatch returns undefined during SSR (see IS_SSR in usePurchaseFormWatch).
    const selectedPurchaseOption = usePurchaseFormWatch('purchaseOption') ?? 'one-time';

    // TODO: Replace it with useEffect, remove handlers
    const { context } = useOptimizely();
    const handleOptiEvent = (e: React.KeyboardEvent | React.MouseEvent) => {
        const input = (e.currentTarget as HTMLInputElement)?.value;
        if (input) {
            context?.trackEvent(`${input}-purchase-click`);
        }
    };

    const { isReady, isLoading, isFetching, isFirstAutoshipEligible } = useAutoshipCustomerStatus();
    const {
        isSubscribed: isSubscribedToItem,
        isReady: isSubscriptionReady,
        isError: isSubscriptionError,
    } = useAutoshipSubscription();

    const oneTimePrice = useOneTimePrice();

    if (!product || !isProductAvailableForAutoship(product)) return null;

    const shouldShowSkeleton =
        isLoading || (isFetching && isFirstAutoshipEligible) || (!isSubscriptionReady && !isSubscriptionError);

    const autoshipDiscountMessages = getAutoshipDiscountMessages({
        autoshipFlag,
        isFirstAutoshipEligible,
        discountDisplay: product?.autoship.discountDisplay,
    });
    const autoshipDiscountSubtext =
        isSubscribedIndicatorEnabled && isSubscribedToItem
            ? "You're already subscribed to this item."
            : autoshipDiscountMessages.autoshipDiscountSubtext;

    const autoshipDiscountSubtextTwo =
        isSubscribedIndicatorEnabled && isSubscribedToItem
            ? undefined
            : autoshipDiscountMessages.autoshipDiscountSubtextTwo;

    return (
        <fieldset className="purchase-options" data-testid="pdp-purchase-options">
            <label
                className={cn('purchase-options__option', {
                    'purchase-options__option--checked': selectedPurchaseOption === 'one-time',
                })}
                htmlFor="one-time"
            >
                <div className="purchase-options__option--title-container">
                    <TextPassage className="purchase-options__option--title" size="lg">
                        One-time purchase
                    </TextPassage>
                    {isPdpDynPxEnabled && oneTimePrice && (
                        <SparkyPrice
                            className="purchase-options__option--price"
                            data-testid="pdp-one-time-price"
                            values={oneTimePrice.values}
                            variant={oneTimePrice.variant}
                        />
                    )}
                </div>
                <input
                    className="radio"
                    type="radio"
                    value="one-time"
                    id="one-time"
                    {...register('purchaseOption')}
                    onKeyDown={handleOptiEvent}
                    onClick={handleOptiEvent}
                />
            </label>
            <label
                className={cn('purchase-options__option', 'purchase-options__option--autoship', {
                    'purchase-options__option--checked': selectedPurchaseOption === 'autoship',
                })}
                htmlFor="autoship"
            >
                <div className="purchase-options__option--autoship-main">
                    <div className="purchase-options__option--body">
                        <div className="purchase-options__option--title-container">
                            <TextPassage className="purchase-options__option--title" size="lg">
                                {shouldShowSkeleton && (
                                    <SkeletonBar className="purchase-options__option--title-skeleton" />
                                )}
                                {!shouldShowSkeleton && <>{autoshipDiscountMessages.autoshipDiscountHeader}</>}
                            </TextPassage>
                            <AutoshipInfoDrawer
                                discountDisplay={product?.autoship.discountDisplay}
                                isFirstAutoshipEligible={isFirstAutoshipEligible}
                            />
                        </div>
                        <div className="purchase-options__option--content">
                            <div className="purchase-options__option--content-text">
                                {shouldShowSkeleton && (
                                    <SkeletonBar className="purchase-options__option--content-text-skeleton" />
                                )}
                                {!shouldShowSkeleton && (
                                    <>
                                        <TextPassage>{autoshipDiscountSubtext}</TextPassage>
                                        {autoshipDiscountSubtextTwo && (
                                            <TextPassage>{autoshipDiscountSubtextTwo}</TextPassage>
                                        )}
                                        {autoshipFlag === 'precrawl' && (
                                            <TextPassage size="sm">Only available with Ship to me.</TextPassage>
                                        )}
                                    </>
                                )}
                            </div>
                            {selectedPurchaseOption === 'autoship' && (
                                <>
                                    <FrequencyOptions />
                                    {isFrequencyCalculatorEnabled && product.isFrequencyCalculatorEligible && (
                                        <FrequencyCalculatorCta />
                                    )}
                                    {autoshipFlag === 'precrawl' ? null : (
                                        <TextPassage className="purchase-options__autoship-text" size="sm">
                                            Choose how you want to receive your first order below.
                                        </TextPassage>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <input
                    className="radio"
                    type="radio"
                    value="autoship"
                    id="autoship"
                    disabled={!isReady}
                    {...register('purchaseOption')}
                    onKeyDown={handleOptiEvent}
                    onClick={handleOptiEvent}
                />
            </label>
        </fieldset>
    );
};
